import { Link } from "react-router-dom";
import { memo } from "react";
import { Linkedin, Github, Mail, Accessibility } from "lucide-react";
import { motion } from "framer-motion";

/**
 * Footer Link Interface
 */
interface FooterLink {
  href: string;
  label: string;
  icon: React.ReactNode;
  external?: boolean;
}

/**
 * Footer Component
 * 
 * Displays site footer with copyright information and social links
 */
const Footer = () => {
  const currentYear = new Date().getFullYear();

  // Social links configuration
  const socialLinks: FooterLink[] = [
    {
      href: "mailto:tomerg.work@gmail.com",
      label: "Email",
      icon: <Mail className="h-4 w-4" />,
    },
    {
      href: "https://github.com/Tgoldi",
      label: "GitHub",
      icon: <Github className="h-4 w-4" />,
      external: true,
    },
    {
      href: "https://www.linkedin.com/in/tomergoldi",
      label: "LinkedIn",
      icon: <Linkedin className="h-4 w-4" />,
      external: true,
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };

  return (
    <motion.footer 
      className="border-t py-4 md:py-8 bg-background/50 backdrop-blur-sm"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-100px" }}
      variants={containerVariants}
    >
      <div className="container-custom">
        {/* Mobile Footer */}
        <div className="md:hidden">
          <div className="flex flex-col gap-4">
            {/* Quick Links */}
            <div className="grid grid-cols-2 gap-2">
              <Link 
                to="/" 
                className="text-sm text-muted-foreground hover:text-foreground transition-colors py-1"
              >
                Home
              </Link>
              <Link 
                to="/projects" 
                className="text-sm text-muted-foreground hover:text-foreground transition-colors py-1"
              >
                Projects
              </Link>
              <Link 
                to="/experience" 
                className="text-sm text-muted-foreground hover:text-foreground transition-colors py-1"
              >
                Experience
              </Link>
              <Link 
                to="/contact" 
                className="text-sm text-muted-foreground hover:text-foreground transition-colors py-1"
              >
                Contact
              </Link>
            </div>

            {/* Social Links */}
            <div className="flex justify-center gap-4 py-2">
              {socialLinks.map((link) => (
                <motion.a
                  key={link.label}
                  href={link.href}
                  aria-label={link.label}
                  target={link.external ? "_blank" : undefined}
                  rel={link.external ? "noopener noreferrer" : undefined}
                  className="text-muted-foreground hover:text-crimson transition-colors p-2"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {link.icon}
                </motion.a>
              ))}
            </div>

            {/* Copyright */}
            <div className="text-center text-xs text-muted-foreground pt-2 border-t border-border">
              <Link 
                to="/" 
                className="font-medium hover:text-crimson transition-colors"
                aria-label="Tomer's homepage"
              >
                <span className="text-crimson">T</span>omer<span className="text-crimson">.</span>
              </Link>
              <span className="block mt-1">&copy; {currentYear} Tomer Goldstein</span>
            </div>
          </div>
        </div>

        {/* Desktop Footer */}
        <div className="hidden md:block">
          <div className="grid grid-cols-3 gap-8 mb-8">
            <motion.div variants={itemVariants}>
              <h3 className="text-lg font-medium mb-3">About</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Portfolio of Tomer Goldstein, a full-stack developer specializing in modern web technologies.
              </p>
            </motion.div>
            
            <motion.div variants={itemVariants}>
              <h3 className="text-lg font-medium mb-3">Links</h3>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link 
                    to="/" 
                    className="text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1 py-1"
                  >
                    Home
                  </Link>
                </li>
                <li>
                  <Link 
                    to="/projects" 
                    className="text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1 py-1"
                  >
                    Projects
                  </Link>
                </li>
                <li>
                  <Link 
                    to="/experience" 
                    className="text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1 py-1"
                  >
                    Experience
                  </Link>
                </li>
                <li>
                  <Link 
                    to="/contact" 
                    className="text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1 py-1"
                  >
                    Contact
                  </Link>
                </li>
                <li>
                  <Link 
                    to="/accessibility" 
                    className="text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1 py-1"
                  >
                    <span className="flex items-center gap-1">
                      <Accessibility className="h-3 w-3" />
                      Accessibility
                    </span>
                  </Link>
                </li>
              </ul>
            </motion.div>
            
            <motion.div variants={itemVariants}>
              <h3 className="text-lg font-medium mb-3">Connect</h3>
              <div className="flex flex-col gap-2">
                {socialLinks.map((link) => (
                  <FooterSocialLink key={link.label} link={link} />
                ))}
              </div>
            </motion.div>
          </div>
          
          <motion.div 
            className="flex justify-between items-center pt-4 border-t border-border"
            variants={itemVariants}
          >
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Link 
                to="/" 
                className="font-medium hover:text-crimson transition-colors"
                aria-label="Tomer's homepage"
              >
                <span className="text-crimson">T</span>omer<span className="text-crimson">.</span>
              </Link>
              <span className="inline-block" aria-hidden="true">•</span>
              <span>&copy; {currentYear} Tomer Goldstein. All rights reserved.</span>
            </div>
          </motion.div>
        </div>
      </div>
    </motion.footer>
  );
};

/**
 * Footer Social Link Component
 */
interface FooterSocialLinkProps {
  link: FooterLink;
}

const FooterSocialLink = ({ link }: FooterSocialLinkProps) => (
  <motion.a
    href={link.href}
    aria-label={link.label}
    target={link.external ? "_blank" : undefined}
    rel={link.external ? "noopener noreferrer" : undefined}
    className="text-muted-foreground hover:text-crimson transition-colors flex items-center gap-2 py-1"
    whileHover={{ scale: 1.02, x: 4 }}
    whileTap={{ scale: 0.98 }}
  >
    {link.icon}
    <span>{link.label}</span>
  </motion.a>
);

export default memo(Footer);
